import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  ImplementedInterfacesBySchemaIdQuery,
  ImplementedInterfacesBySchemaIdQueryVariables,
} from 'processes/tables/column-mapping/__generated/ImplementedInterfacesBySchemaIdQuery';

import {SchemaIdToImplementedInterfaces} from './fieldsetInterfaceMatching';

export const IMPLEMENTED_INTERFACES_BY_SCHEMA_ID_QUERY = gql`
  query ImplementedInterfacesBySchemaIdQuery @skipAudit @engComponent(name: WORKFLOWS) {
    tagSchemas {
      id
      interfaces {
        id
      }
    }
  }
`;

/**
 * Each entity schema id -> the schema-interface (incl. fieldset) ids it implements, for the "Create
 * tasks" column-mapping flow. Pass `skip` when the task schema has no fieldset field, so the whole-schema
 * fetch only runs when it can matter. Returns an empty map while pending or on error (caller degrades to
 * "no fieldset match" rather than blocking).
 */
export function useImplementedInterfacesBySchemaId(skip: boolean): {
  interfacesBySchemaId: SchemaIdToImplementedInterfaces;
  isPending: boolean;
} {
  const result = useSkippableBenchlingQuery<
    ImplementedInterfacesBySchemaIdQuery,
    ImplementedInterfacesBySchemaIdQueryVariables
  >(IMPLEMENTED_INTERFACES_BY_SCHEMA_ID_QUERY, {skip, renderCachedDataWhenRefetching: true});

  // `result` is a fresh object every render, so key the memo on the (Apollo-cache-stable) data itself to
  // avoid rebuilding the map and its Sets when nothing changed.
  const tagSchemas = result.type === QueryResultType.SUCCESS ? result.data.tagSchemas : null;
  const interfacesBySchemaId = useMemo<SchemaIdToImplementedInterfaces>(() => {
    if (tagSchemas == null) {
      return {};
    }
    return Object.fromEntries(
      tagSchemas.map((schema) => [
        schema.id,
        new Set(schema.interfaces.map((schemaInterface) => schemaInterface.id)),
      ])
    );
  }, [tagSchemas]);

  return {interfacesBySchemaId, isPending: result.type === QueryResultType.PENDING};
}
